import { ALL_EQUIPMENTS, MIXED_PRODUCTS } from '../../../shared/types/oeeRow.ts';
import { ShiftSlot } from '../../../shared/types/shiftTypes.ts';
import { mapBy, maxBy, minBy } from '../../../shared/utils/arrayUtils.ts';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { OeeRow } from '../../types/sharedTypeImpl.ts';
import { getGraphValue, getGraphValueAverage } from '../../utils/ooeGraphValueType.ts';
import { LiveGraphSettings } from '../live-graph-settings/liveGraphSettings.ts';


export interface MainData {
    shift: ShiftSlot;
    shiftIndex: number;
    start: Date;
    end: Date;
    isSetup: boolean;
    isDisconnect: boolean;
    isCurrentProduct: boolean;
    weight: number;
    value: number;
    rollingAverageValue: number;
}

export interface ShiftData {
    shift: ShiftSlot;
    firstOeeRow: OeeRow;
    lastOeeRow: OeeRow;
    value: number;
    label: string;
}

export interface TransformedData {
    currentProduct: string | null;
    currentPartDisplayName: string | null;
    mainData: MainData[];
    shiftData: ShiftData[];
}


export function transformData(
    equipmentLiveData: OeeRow[],
    equipmentHourData: OeeRow[],
    equipmentShiftData: OeeRow[],
    displayOptions: LiveGraphSettings,
): TransformedData {
    const mainOeeData = selectMainData(equipmentLiveData, equipmentHourData, equipmentShiftData, displayOptions);
    const { product, partDisplayName } = getCurrentProduct(mainOeeData);
    const filteredData = filterInputRows(mainOeeData, displayOptions);

    const mainData = getMainData(filteredData, product, displayOptions);
    const shiftData = getShiftData(filteredData, equipmentShiftData, displayOptions);
    return {
        currentProduct: product,
        currentPartDisplayName: partDisplayName,
        mainData,
        shiftData,
    }
}

function selectMainData(
    equipmentLiveData: OeeRow[],
    equipmentHourData: OeeRow[],
    equipmentShiftData: OeeRow[],
    displayOptions: LiveGraphSettings,
): OeeRow[] {
    switch (displayOptions.resolution) {
        case 'halfHour': return equipmentLiveData;
        case 'hour': return equipmentHourData;
        case 'shift': return equipmentShiftData;
    }
}

function filterInputRows(
    equipmentMainData: OeeRow[],
    displayOptions: LiveGraphSettings,
): OeeRow[] {
    const offsetHours = (displayOptions.timeFrame?.days ?? 0) * 24 + (displayOptions.timeFrame?.hours ?? 0);
    const start = new Date(+new Date() - offsetHours * 3600_000);

    const filtered = equipmentMainData
        .filter(it => displayOptions.visibleShifts.includes(it.shiftSlot))
        .filter(it => !displayOptions.excludeWeekends || it.shiftCode !== 'W')
        .filter(it => it.end >= start);
    const sorted = sortBy(filtered, it => it.start);
    return sorted;
}

/**
 * Determine the current product based on the latest OEE row. This is used to highlight the relevant OEE rows in the graph and to calculate the average OEE for the current product.
 * @param equipmentMainData 
 * @returns product and part display name of the current product.
 */
function getCurrentProduct(
    equipmentMainData: OeeRow[]
): {
    product: string | null,
    partDisplayName: string | null,
} {
    const latestRow = maxBy(equipmentMainData, it => +it.start);

    return {
        product: latestRow?.product ?? null,
        partDisplayName: latestRow?.partDisplayName ?? null,
    };
}

function getMainData(
    equipmentMainData: OeeRow[],
    currentProduct: string | null,
    displayOptions: LiveGraphSettings,
): MainData[] {
    const mainDataPartial = equipmentMainData.map(it => ({
        shift: it.shiftSlot,
        shiftIndex: getShiftIndex(it),
        start: it.start,
        end: it.end,
        isSetup: it.connectedTime > 0 && it.setupTime === it.connectedTime,
        isDisconnect: it.connectedTime == 0,
        isCurrentProduct: (it.product === currentProduct && it.product !== MIXED_PRODUCTS) || it.equipment === ALL_EQUIPMENTS,
        weight: getGraphValue(it, displayOptions.valueType, displayOptions.oeeComponents).weight,
        value: getGraphValue(it, displayOptions.valueType, displayOptions.oeeComponents).value,
    }));

    sortBy(mainDataPartial, it => it.start);
    const mainData = smoothMainData(mainDataPartial, displayOptions);
    return mainData;
}

function smoothMainData(
    mainData: Omit<MainData, 'rollingAverageValue'>[],
    displayOptions: LiveGraphSettings,
): MainData[] {
    const amount = displayOptions.smoothingAmount!;

    const smoothedData = mainData.map(it => ({ ...it, rollingAverageValue: 0 }));
    for (let i = 0; i < smoothedData.length; i++) {
        if (smoothedData[i].weight == 0) {
            smoothedData[i].rollingAverageValue = 0;
            continue;
        }

        let sum = 0;
        let count = 0;
        for (let j = i - amount; j <= i + amount; j++) {
            if (j < 0 || j >= smoothedData.length) continue;
            if (smoothedData[j].shiftIndex !== smoothedData[i].shiftIndex) continue;// Only consider the same shift
            sum += smoothedData[j].value * smoothedData[j].weight;
            count += smoothedData[j].weight;
        }
        smoothedData[i].rollingAverageValue = sum / count;
    }
    return smoothedData;
}

function getShiftData(
    equipmentMainData: OeeRow[],
    equipmentShiftData: OeeRow[],
    displayOptions: LiveGraphSettings,
): ShiftData[] {
    const mainDataByShiftIndex = mapBy(equipmentMainData, it => getShiftIndex(it));
    const shiftDataByShiftIndex = mapBy(equipmentShiftData, it => getShiftIndex(it));

    const shiftData: ShiftData[] = [];
    for (const shiftIndex of mainDataByShiftIndex.keys()) {
        const mainData = mainDataByShiftIndex.get(shiftIndex)!;
        const shiftRow = shiftDataByShiftIndex.get(shiftIndex)?.[0] ?? null;

        const shift = mainData[0].shiftSlot;
        const firstMainEntry = minBy(mainData, it => +it.start)!;
        const lastMainEntry = maxBy(mainData, it => +it.start)!;

        // Use the shift data if available. Othewise use the average of the main data.
        // Prefer using the shift data to stay consistent with the factory shifts graph.
        const { value } = shiftRow != null
            ? getGraphValue(shiftRow, displayOptions.valueType, displayOptions.oeeComponents)
            : getGraphValueAverage(mainData, displayOptions.valueType, displayOptions.oeeComponents);

        shiftData.push({
            shift,
            firstOeeRow: firstMainEntry,
            lastOeeRow: lastMainEntry,
            value,
            label: value.toFixed(2),
        });
    }
    return shiftData;
}

function getShiftIndex(oeeRow: OeeRow): number {
    const shiftTypeIndex =
        oeeRow.shiftSlot === 'night' ? 0
            : oeeRow.shiftSlot === 'morning' ? 1
                : 2;

    const hoursOffset = oeeRow.shiftSlot === 'night' ? 12 : 0;
    const dayIndex = getUTCDayOfYear(oeeRow.start, hoursOffset);

    return 3 * dayIndex + shiftTypeIndex;
}

function getUTCDayOfYear(date: Date, plusHours: number): number {
    const dateFinal = new Date(date.getTime() + (plusHours ?? 0) * 3600 * 1000)
    const startOfYear = new Date(Date.UTC(dateFinal.getUTCFullYear(), 0, 1));
    const diff = dateFinal.getTime() - startOfYear.getTime();
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.floor(diff / oneDay);
}
